"use client";

import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@/components/ui/drawer";
import type { PushNudgeCopy } from "../lib/push-nudge-copy";
import {
  PushNudgeMedallion,
  PushNudgeMockRow,
  PushNudgeReassurance,
} from "./push-nudge-parts";

export type PushNudgeSheetProps = {
  open: boolean;
  copy: PushNudgeCopy;
  isBusy?: boolean;
  onPrimary: () => void;
  onDismiss: () => void;
};

/**
 * V1 — the celebration sheet.
 *
 * Reserved for moments the user is already pleased about: a booking that just
 * confirmed, a session just joined. It is the most interruptive shell in the
 * kit, which is why it is spent only where the interruption reads as part of
 * the good news rather than an ad stapled to it.
 *
 * Built on the app's Drawer (vaul) rather than a hand-rolled sheet so it
 * inherits real dialog semantics — focus trap, escape, backdrop dismissal — for
 * free. Escaping and tapping the scrim both route to `onDismiss`, which is the
 * same thing "Not now" does: nothing is charged, and the surface re-qualifies.
 */
export function PushNudgeSheet({
  open,
  copy,
  isBusy = false,
  onPrimary,
  onDismiss,
}: PushNudgeSheetProps) {
  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
    >
      <DrawerContent className="px-5 pb-[max(env(safe-area-inset-bottom),1.25rem)]">
        <div className="mx-auto flex w-full max-w-md flex-col items-center gap-4 pt-2">
          <PushNudgeMedallion size="lg" />

          <div className="space-y-1 text-center">
            <DrawerTitle className="text-[20px] leading-tight font-semibold text-balance text-text-primary">
              {copy.headline}
            </DrawerTitle>
            {copy.subhead ? (
              <DrawerDescription className="text-[14px] leading-snug text-text-secondary">
                {copy.subhead}
              </DrawerDescription>
            ) : null}
          </div>

          {copy.mockRows.length > 0 ? (
            <div className="flex w-full flex-col gap-2">
              {copy.mockRows.map((row, index) => (
                <PushNudgeMockRow
                  key={`${row.title}-${row.body}`}
                  row={row}
                  index={index}
                />
              ))}
            </div>
          ) : null}

          <div className="flex w-full flex-col gap-1.5">
            <Button
              onClick={onPrimary}
              disabled={isBusy}
              className="h-[52px] w-full rounded-full bg-brand text-[16px] font-semibold text-brand-foreground transition-transform hover:bg-brand-hover active:scale-[0.99] disabled:opacity-60 dark:bg-brand dark:text-brand-foreground"
            >
              {isBusy ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                copy.primaryLabel
              )}
            </Button>

            <Button
              variant="ghost"
              onClick={onDismiss}
              disabled={isBusy}
              className="h-11 w-full rounded-full text-[15px] font-medium text-text-secondary hover:bg-glass-bg-04 dark:text-text-secondary dark:hover:bg-glass-bg-06"
            >
              {copy.dismissLabel}
            </Button>
          </div>

          <PushNudgeReassurance>{copy.reassurance}</PushNudgeReassurance>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
